import React, { Component, createRef } from 'react';
import { observable, action, makeObservable } from 'mobx';
import { observer } from 'mobx-react';

interface ToggleModeButtonProps {
  title: string;
  alternativeTitle: string;
  isAlternativeMode: boolean;
  onModeSwitch?: (isAlternative: boolean) => void;
  onClick?: () => void;
  onClickAlternative?: () => void;
  switchTimeMs?: number;
  className?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
  visible?: boolean;
}

//based on https://codepen.io/vitor-siqueira/pen/xNBExN?editors=1100
@observer
class ToggleModeButton extends Component<ToggleModeButtonProps> {
  @observable isAnimating = false;
  @observable isPressed = false;
  private _wasAnimating = false;

  private buttonRef = createRef<HTMLButtonElement>();
  private svgRef = createRef<SVGSVGElement>();
  private pressTimer: number | null = null;

  constructor(props: ToggleModeButtonProps) {
    super(props);
    makeObservable(this);
  }

  componentWillUnmount() {
    this.clearTimers();
  }

  private clearTimers() {
    if (this.pressTimer) {
      clearTimeout(this.pressTimer);
      this.pressTimer = null;
    }
  }

  @action
  private handleAnimationEnd = () => {
    this.props.onModeSwitch?.(!this.props.isAlternativeMode);
    this.isAnimating = false;
    navigator.vibrate?.(200); // Optional vibration feedback for mobile devices
  };

  @action
  private handleMouseDown = () => {
    if (this.props.disabled) return;
    
    this.isPressed = true;
    const switchTime = this.props.switchTimeMs || 1000;
    
    this.pressTimer = window.setTimeout(() => {
      this.startingModeSwitch();
    }, switchTime);
  };

  @action
  private handleMouseUp = () => {
    if (this.props.disabled) return;
    // If the button wasn't pressed, do nothing
    if (!this.isPressed) return;
    
    this.isPressed = false;
    
    if (this.pressTimer) {
      // Short press - trigger appropriate callback
      clearTimeout(this.pressTimer);
      this.pressTimer = null;
      
      const { isAlternativeMode } = this.props;
      if (!this.isAnimating && !this._wasAnimating) {
        if (isAlternativeMode && this.props.onClickAlternative) {
          this.props.onClickAlternative();
        } else if (!isAlternativeMode && this.props.onClick) {
          this.props.onClick();
        }
      }
    }
    
    this._wasAnimating = false;
    this.isAnimating = false;
  };

  @action
  private handleMouseLeave = () => {
    if (this.props.disabled) return;
    
    this.isPressed = false;
    this.isAnimating = false;
    
    if (this.pressTimer) {
      clearTimeout(this.pressTimer);
      this.pressTimer = null;
    }
  };

  @action
  private startingModeSwitch = () => {
    this.isAnimating = true;
    this._wasAnimating = true;
  };

  private getButtonDimensions() {
    if (this.buttonRef.current) {
      const rect = this.buttonRef.current.getBoundingClientRect();
      return {
        width: rect.width,
        height: rect.height
      };
    }
    return { width: 180, height: 60 }; // fallback
  }

  render() {
    const { 
      title, 
      alternativeTitle, 
      className = '', 
      style = {}, 
      disabled = false,
      visible = true
    } = this.props;

    if (!visible) {
      return null;
    }

    const dimensions = this.getButtonDimensions();
    const perimeter = 2 * (dimensions.width + dimensions.height - 4);
    const strokeDashArray = `${perimeter * 0.25} ${perimeter}`;

    const buttonClasses = `
      toggle-mode-btn 
      ${this.props.isAlternativeMode ? 'alternative-mode' : 'normal-mode'}
      ${this.isAnimating ? 'animating' : ''}
      ${this.isPressed ? 'pressed' : ''}
      ${disabled ? 'disabled' : ''}
      ${className}
    `.trim();

    return (
      <>
        <style>{`
          .toggle-mode-btn {
            position: relative;
            cursor: pointer;
            border: 1px solid var(--border-color, #404040);
            outline: none;
            transition: 1s ease-in-out;
            overflow: hidden;
            user-select: none;
          }

          .toggle-mode-btn.disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }

          .toggle-mode-btn svg {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            height: 100%;
            fill: none;
            stroke: var(--accent-color, #007acc);
            strokeWidth: 2;
            pointer-events: none;
          }

          .toggle-mode-btn:not(.animating) svg {
            stroke-dashoffset: 0;
            transition: stroke-dashoffset 1s ease-in-out;
          }

          .toggle-mode-btn.animating svg {
            stroke: var(--warning-color, transparent);
            animation: dash-border 1s linear forwards;
          }

          .toggle-mode-btn.alternative-mode svg {
            stroke: var(--text-primary, transparent);
          }

          .toggle-mode-btn span {
            position: relative;
            z-index: 1;
            display: block;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          @keyframes dash-border {
            from {
              stroke-dashoffset: 0;
            }
            to {
              stroke-dashoffset: calc(-1 * var(--dash-total));
            }
          }

          .toggle-mode-btn.pressed {
            transform: scale(0.98);
          }
        `}</style>
        
        <button
          ref={this.buttonRef}
          className={buttonClasses}
          style={{
            ...style,
            '--dash-total': `${perimeter + (perimeter * 0.25)}px`
          } as React.CSSProperties}
          onMouseDown={this.handleMouseDown}
          onMouseUp={this.handleMouseUp}
          onMouseLeave={this.handleMouseLeave}
          onTouchStart={this.handleMouseDown}
          onTouchEnd={this.handleMouseUp}
          onAnimationEnd={this.handleAnimationEnd}
          disabled={disabled}
          type="button"
        >
          {this.isAnimating && <svg 
            ref={this.svgRef}
            viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
          >
            <polyline
              points={`2,2 ${dimensions.width-2},2 ${dimensions.width-2},${dimensions.height-2} 2,${dimensions.height-2} 2,2`}
              strokeDasharray={strokeDashArray}
            />
          </svg>}
          <span>
            {this.props.isAlternativeMode ? alternativeTitle : title}
          </span>
        </button>
      </>
    );
  }
}

export default ToggleModeButton;